/**
 * React hook for mobile device detection
 * Safely detects mobile/tablet devices with window resize support
 */

import { useState, useEffect } from 'react'
import {
  getDeviceType,
  isMobileDevice,
  isPhoneDevice,
} from '../utils/deviceDetection'

interface MobileDetectionResult {
  isMobile: boolean
  isPhone: boolean
  deviceType: 'mobile' | 'tablet' | 'desktop'
}

/**
 * Custom hook to detect if user is on a mobile/tablet device
 * Responds to window resize events for accuracy
 *
 * @returns Object with isMobile flag and deviceType string
 */
export function useMobileDetection(): MobileDetectionResult {
  const [isMobile, setIsMobile] = useState(() => isMobileDevice())
  const [isPhone, setIsPhone] = useState(() => isPhoneDevice())
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>(
    () => getDeviceType()
  )

  useEffect(() => {
    // Update detection on window resize (e.g., device rotation)
    const handleResize = () => {
      setIsMobile(isMobileDevice())
      setIsPhone(isPhoneDevice())
      setDeviceType(getDeviceType())
    }

    // Debounce resize events to avoid excessive re-renders
    let timeoutId: NodeJS.Timeout
    const debouncedResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleResize, 150)
    }

    window.addEventListener('resize', debouncedResize)

    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(timeoutId)
    }
  }, [])

  return { isMobile, isPhone, deviceType }
}
